import { useEffect, useState } from 'react';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

export default function ShareModal({ open, onClose, title }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) setCopied(false);
  }, [open]);

  if (!open) return null;

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const socials = [
    { key: 'weibo', label: '微博', icon: 'ri-weibo-fill', url: `https://service.weibo.com/share/share.php?title=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}` },
    { key: 'twitter', label: 'X', icon: 'ri-twitter-x-fill', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}` },
    { key: 'facebook', label: 'Facebook', icon: 'ri-facebook-fill', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { key: 'qq', label: 'QQ', icon: 'ri-qq-fill', url: `https://connect.qq.com/widget/shareqq/index.html?title=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}` },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-foreground-950/50" onClick={onClose}></div>
      <div className="relative w-full max-w-md rounded-lg bg-background-50 border border-background-200 p-6 animate-fade-up">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">分享《{title}》</h3>
            <p className="text-xs text-foreground-500 mt-1">复制链接或分享到社交平台</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer transition-colors"
            aria-label="关闭"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex items-center gap-2 mb-5">
          <input
            readOnly
            value={shareUrl}
            className="flex-1 min-w-0 px-3 py-2.5 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-700"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
            {copied ? '已复制' : '复制'}
          </button>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {socials.map((social) => (
            <a
              key={social.key}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 py-3 rounded-md bg-background-100 hover:bg-primary-50 hover:text-primary-600 text-foreground-700 cursor-pointer transition-colors"
            >
              <i className={`${social.icon} text-xl`}></i>
              <span className="text-xs">{social.label}</span>
            </a>
          ))}
        </div>

        {copied && (
          <p className="mt-4 text-xs text-primary-600 flex items-center gap-1">
            <i className="ri-checkbox-circle-line"></i>
            链接已复制到剪贴板
          </p>
        )}
      </div>
    </div>
  );
}